import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  Link,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import { salonApi, Salon } from '../services/salonService';
import { serviceApi, Service, formatServicePrice, formatServiceDuration } from '../services/serviceService';
import { staffApi, Staff } from '../services/staffService';
import { useAuthStore } from '../store/authStore';
import { useSalonContextStore } from '../store/salonContextStore';
import {
  PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, FieldGrid, useConfirm, Crumb,
} from '../components/ui';
import { tokens } from '../theme';
import { countLabel, formatDuration } from '../utils/format';

const DAY_NAMES = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];

const apiErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { error?: string; details?: { message: string }[] } } })?.response?.data;
  if (data?.details?.length) return data.details.map((d) => d.message).join(' ');
  return data?.error || fallback;
};

const priceLabel = (svc: Service) => formatServicePrice(svc);

const durationLabel = (svc: Service) => {
  if (svc.variants && svc.variants.length > 0) return formatServiceDuration(svc);
  const max = svc.durationMax != null ? Number(svc.durationMax) : null;
  return max && max > svc.duration ? `${formatDuration(svc.duration)} – ${formatDuration(max)}` : formatDuration(svc.duration);
};

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: { xs: 2, md: 2.5 },
  py: 1.5,
  borderTop: `1px solid ${tokens.line}`,
  '&:first-of-type': { borderTop: 'none' },
} as const;

export const SalonDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const role = useAuthStore((s) => s.user?.role);
  const setActiveSalonId = useSalonContextStore((s) => s.setActiveSalonId);
  const [salon, setSalon] = useState<Salon | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialogs
  const [serviceDialog, setServiceDialog] = useState(false);
  const [staffDialog, setStaffDialog] = useState(false);

  // Service form
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: '',
  });

  // Staff form
  const [staffForm, setStaffForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
  });

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const [salonData, servicesData, staffData] = await Promise.all([
          salonApi.getById(id),
          serviceApi.listBySalon(id).catch(() => []),
          staffApi.listBySalon(id).catch(() => []),
        ]);
        setSalon(salonData);
        setServices(servicesData);
        setStaffList(staffData);
      } catch {
        setError('Nie udało się pobrać danych salonu');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleAddService = async () => {
    if (!id) return;
    try {
      await serviceApi.create({
        salonId: id,
        name: serviceForm.name,
        price: parseFloat(serviceForm.price),
        duration: parseInt(serviceForm.duration, 10),
        ...(serviceForm.description ? { description: serviceForm.description } : {}),
        ...(serviceForm.category ? { category: serviceForm.category } : {}),
      });
      setServiceDialog(false);
      setError('');
      setServiceForm({ name: '', description: '', price: '', duration: '', category: '' });
      const updated = await serviceApi.listBySalon(id);
      setServices(updated);
    } catch (err) {
      setError(apiErrorMessage(err, 'Nie udało się dodać usługi. Sprawdź dane i spróbuj ponownie.'));
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      await serviceApi.delete(serviceId);
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
    } catch {
      setError('Nie udało się usunąć usługi. Spróbuj ponownie za chwilę.');
    }
  };

  const handleAddStaff = async () => {
    if (!id) return;
    try {
      await staffApi.add({
        salonId: id,
        email: staffForm.email,
        firstName: staffForm.firstName,
        lastName: staffForm.lastName,
        ...(staffForm.phone ? { phone: staffForm.phone } : {}),
      });
      setStaffDialog(false);
      setError('');
      setStaffForm({ email: '', firstName: '', lastName: '', phone: '' });
      const updated = await staffApi.listBySalon(id);
      setStaffList(updated);
    } catch (err) {
      setError(apiErrorMessage(err, 'Nie udało się dodać pracownika. Sprawdź dane i spróbuj ponownie.'));
    }
  };

  const handleRemoveStaff = async (staffId: string) => {
    try {
      await staffApi.remove(staffId);
      setStaffList((prev) => prev.filter((s) => s.id !== staffId));
    } catch {
      setError('Nie udało się usunąć pracownika. Spróbuj ponownie za chwilę.');
    }
  };

  const askDeleteService = async (svc: Service) => {
    const ok = await confirm({
      title: 'Usunąć usługę?',
      details: <><strong>{svc.name}</strong> · {priceLabel(svc)} · {durationLabel(svc)}</>,
      description: 'Usługa zniknie z oferty salonu i nie będzie można jej zarezerwować.',
      confirmLabel: 'Usuń usługę',
      tone: 'danger',
    });
    if (ok) handleDeleteService(svc.id);
  };

  const askRemoveStaff = async (member: Staff) => {
    const ok = await confirm({
      title: 'Usunąć osobę z zespołu?',
      details: <><strong>{member.firstName} {member.lastName}</strong>{member.user?.email ? ` · ${member.user.email}` : ''}</>,
      description: 'Osoba zniknie z zespołu tego salonu i z wyboru specjalisty przy rezerwacji.',
      confirmLabel: 'Usuń z zespołu',
      tone: 'danger',
    });
    if (ok) handleRemoveStaff(member.id);
  };

  const canManage = role === 'SALON_OWNER' || role === 'SUPER_ADMIN';
  const isClient = role === 'CLIENT';

  let breadcrumbs: Crumb[] = [{ label: 'Salony', to: '/salons' }];
  if (role === 'SUPER_ADMIN') breadcrumbs = [{ label: 'Administracja' }, { label: 'Katalog salonów', to: '/salons' }];
  else if (role === 'SALON_OWNER') breadcrumbs = [{ label: 'Zarządzaj salonami', to: '/salons' }];
  else if (isClient) breadcrumbs = [{ label: 'Znajdź salon', to: '/salons' }];

  if (loading) {
    return (
      <Stack gap={2.5} aria-busy="true">
        <LoadingState variant="table" rows={5} />
        <LoadingState variant="block" height={140} />
      </Stack>
    );
  }

  if (!salon) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Nie znaleziono salonu" />
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Ten salon nie istnieje albo nie jest już dostępny"
            description={error || 'Sprawdź adres strony albo wybierz salon z listy.'}
            actions={<Button variant="outlined" onClick={() => navigate('/salons')}>Wróć do listy salonów</Button>}
          />
        </SectionCard>
      </Box>
    );
  }

  const visibleServices = canManage ? services : services.filter((s) => s.isActive);
  const visibleStaff = canManage ? staffList : staffList.filter((s) => s.isActive);

  // Uslugi przychodza posortowane po kategorii — grupujemy kolejno wystepujace.
  const serviceGroups: { category: string; items: Service[] }[] = [];
  visibleServices.forEach((svc) => {
    const category = svc.category || 'Pozostałe';
    const last = serviceGroups[serviceGroups.length - 1];
    if (last && last.category === category) last.items.push(svc);
    else serviceGroups.push({ category, items: [svc] });
  });
  const showCategories = serviceGroups.length > 1 || (serviceGroups[0] && serviceGroups[0].category !== 'Pozostałe');

  const hours = [...(salon.workingHours || [])].sort((a, b) => ((a.dayOfWeek + 6) % 7) - ((b.dayOfWeek + 6) % 7));
  const today = new Date().getDay();
  const dialogOpen = serviceDialog || staffDialog;

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={salon.name}
        description={`${salon.address}, ${salon.postalCode} ${salon.city}`}
        actions={
          isClient ? (
            <Button variant="contained" startIcon={<EventAvailableOutlinedIcon />} onClick={() => navigate(`/booking?salonId=${salon.id}`)}>
              Zarezerwuj wizytę
            </Button>
          ) : role === 'SALON_OWNER' ? (
            <Button
              variant="outlined"
              startIcon={<TuneOutlinedIcon />}
              onClick={() => { setActiveSalonId(salon.id); navigate('/salon/profile'); }}
            >
              Ustawienia salonu
            </Button>
          ) : undefined
        }
      />

      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Stack gap={2.5} sx={{ minWidth: 0 }}>
          {/* USLUGI */}
          <SectionCard
            title="Usługi"
            subtitle={countLabel(visibleServices.length, 'usługa', 'usługi', 'usług')}
            icon={<ContentCutOutlinedIcon />}
            padding="none"
            action={canManage ? (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setError(''); setServiceDialog(true); }}>
                Dodaj usługę
              </Button>
            ) : undefined}
          >
            {visibleServices.length === 0 ? (
              <EmptyState
                compact
                icon={<ContentCutOutlinedIcon />}
                title="Salon nie ma jeszcze usług"
                description={canManage ? 'Dodaj pierwszą usługę — klienci zobaczą ją przy rezerwacji.' : 'Usługi pojawią się tutaj, gdy salon je doda.'}
              />
            ) : (
              serviceGroups.map((group, gi) => (
                <Box key={group.category}>
                  {showCategories && (
                    <Typography
                      sx={{
                        px: { xs: 2, md: 2.5 },
                        py: 0.75,
                        bgcolor: tokens.surface,
                        borderTop: gi > 0 ? `1px solid ${tokens.line}` : 'none',
                        borderBottom: `1px solid ${tokens.line}`,
                        fontSize: '0.8125rem',
                        fontWeight: 650,
                        color: tokens.muted,
                      }}
                    >
                      {group.category}
                    </Typography>
                  )}
                  {group.items.map((svc) => (
                    <Box key={svc.id} sx={rowSx}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{svc.name}</Typography>
                          {!svc.isActive && <StatusPill label="Nieaktywna" tone="neutral" size="small" />}
                        </Stack>
                        {(svc.publicDescription || svc.description) && (
                          <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, mt: 0.25 }}>
                            {svc.publicDescription || svc.description}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {priceLabel(svc)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, whiteSpace: 'nowrap' }}>{durationLabel(svc)}</Typography>
                      </Box>
                      {canManage && (
                        <Tooltip title="Usuń usługę">
                          <IconButton size="small" aria-label={`Usuń usługę ${svc.name}`} onClick={() => askDeleteService(svc)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </SectionCard>

          {/* ZESPOL */}
          <SectionCard
            title="Zespół"
            subtitle={countLabel(visibleStaff.length, 'osoba', 'osoby', 'osób')}
            icon={<GroupsOutlinedIcon />}
            padding="none"
            action={canManage ? (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setError(''); setStaffDialog(true); }}>
                Dodaj pracownika
              </Button>
            ) : undefined}
          >
            {visibleStaff.length === 0 ? (
              <EmptyState
                compact
                icon={<GroupsOutlinedIcon />}
                title="Zespół jest pusty"
                description={canManage ? 'Dodaj pracownika — dostanie zaproszenie do panelu i pojawi się w rezerwacjach.' : 'Salon nie przedstawił jeszcze swojego zespołu.'}
              />
            ) : (
              visibleStaff.map((member) => {
                const secondary = canManage
                  ? [member.user?.email, member.phone].filter(Boolean).join(' · ')
                  : member.specialization || member.bio;
                return (
                  <Box key={member.id} sx={rowSx}>
                    <PersonAvatar firstName={member.firstName} lastName={member.lastName} seed={member.id} size={38} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>
                          {member.firstName} {member.lastName}
                        </Typography>
                        {!member.isActive && <StatusPill label="Nieaktywny" tone="neutral" size="small" />}
                      </Stack>
                      {secondary && (
                        <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, wordBreak: 'break-word' }}>{secondary}</Typography>
                      )}
                    </Box>
                    {canManage && (
                      <Tooltip title="Usuń z zespołu">
                        <IconButton size="small" aria-label={`Usuń ${member.firstName} ${member.lastName} z zespołu`} onClick={() => askRemoveStaff(member)}>
                          <PersonRemoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })
            )}
          </SectionCard>
        </Stack>

        <Stack gap={2.5} sx={{ minWidth: 0 }}>
          {/* KONTAKT */}
          <SectionCard title="O salonie">
            {salon.description && (
              <Typography sx={{ fontSize: '0.9375rem', color: tokens.inkSoft, mb: 2 }}>{salon.description}</Typography>
            )}
            <Stack gap={1.25}>
              <Stack direction="row" gap={1.25} alignItems="flex-start">
                <PlaceOutlinedIcon sx={{ fontSize: 20, color: tokens.muted, mt: 0.25 }} />
                <Typography sx={{ fontSize: '0.9375rem' }}>
                  {salon.address}<br />{salon.postalCode} {salon.city}
                </Typography>
              </Stack>
              {salon.phone && (
                <Stack direction="row" gap={1.25} alignItems="center">
                  <PhoneOutlinedIcon sx={{ fontSize: 20, color: tokens.muted }} />
                  <Link href={`tel:${salon.phone.replace(/\s/g, '')}`} sx={{ fontSize: '0.9375rem' }}>{salon.phone}</Link>
                </Stack>
              )}
              {salon.email && (
                <Stack direction="row" gap={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <MailOutlineOutlinedIcon sx={{ fontSize: 20, color: tokens.muted }} />
                  <Link href={`mailto:${salon.email}`} sx={{ fontSize: '0.9375rem', wordBreak: 'break-all' }}>{salon.email}</Link>
                </Stack>
              )}
            </Stack>
          </SectionCard>

          {/* GODZINY OTWARCIA */}
          <SectionCard title="Godziny otwarcia" icon={<ScheduleOutlinedIcon />} padding="none">
            {hours.length === 0 ? (
              <EmptyState compact title="Godziny otwarcia nie zostały ustawione" />
            ) : (
              <Box sx={{ py: 0.75 }}>
                {hours.map((wh) => {
                  const isToday = wh.dayOfWeek === today;
                  return (
                    <Stack
                      key={wh.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                      sx={{ px: { xs: 2, md: 2.5 }, py: 0.9, bgcolor: isToday ? tokens.plumSofter : 'transparent' }}
                    >
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: isToday ? 700 : 500, color: isToday ? tokens.plum : tokens.ink }}>
                        {DAY_NAMES[wh.dayOfWeek]}
                        {isToday && (
                          <Box component="span" sx={{ ml: 0.75, fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted }}>dziś</Box>
                        )}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.9375rem',
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: isToday ? 700 : 500,
                          color: wh.isClosed ? tokens.muted : tokens.ink,
                        }}
                      >
                        {wh.isClosed ? 'Zamknięte' : `${wh.openTime}–${wh.closeTime}`}
                      </Typography>
                    </Stack>
                  );
                })}
              </Box>
            )}
          </SectionCard>
        </Stack>
      </Box>

      {/* ADD SERVICE DIALOG */}
      <Dialog open={serviceDialog} onClose={() => setServiceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj usługę</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack gap={2}>
            <TextField
              fullWidth
              required
              autoFocus
              label="Nazwa usługi"
              placeholder="np. Strzyżenie damskie"
              value={serviceForm.name}
              onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
            />
            <FieldGrid columns={2}>
              <TextField
                fullWidth
                required
                label="Cena"
                type="number"
                value={serviceForm.price}
                onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
              />
              <TextField
                fullWidth
                required
                label="Czas trwania"
                type="number"
                value={serviceForm.duration}
                onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })}
                inputProps={{ min: 5, max: 480, step: 5 }}
                InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                helperText="Od 5 do 480 minut."
              />
            </FieldGrid>
            <TextField
              fullWidth
              label="Kategoria"
              placeholder="np. Fryzjerstwo"
              value={serviceForm.category}
              onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
              helperText="Usługi z tej samej kategorii są pokazywane razem."
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Opis"
              value={serviceForm.description}
              onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
              helperText="Opcjonalnie — co obejmuje usługa."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setServiceDialog(false)}>Anuluj</Button>
          <Button variant="contained" onClick={handleAddService}>
            Dodaj usługę
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD STAFF DIALOG */}
      <Dialog open={staffDialog} onClose={() => setStaffDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj pracownika</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack gap={2}>
            <TextField
              fullWidth
              required
              autoFocus
              label="Adres e-mail"
              type="email"
              value={staffForm.email}
              onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
              helperText="Jeśli ta osoba nie ma jeszcze konta, wyślemy na ten adres zaproszenie do panelu."
            />
            <FieldGrid columns={2}>
              <TextField
                fullWidth
                required
                label="Imię"
                value={staffForm.firstName}
                onChange={(e) => setStaffForm({ ...staffForm, firstName: e.target.value })}
              />
              <TextField
                fullWidth
                required
                label="Nazwisko"
                value={staffForm.lastName}
                onChange={(e) => setStaffForm({ ...staffForm, lastName: e.target.value })}
              />
            </FieldGrid>
            <TextField
              fullWidth
              label="Telefon"
              type="tel"
              value={staffForm.phone}
              onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
              helperText="Opcjonalnie."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setStaffDialog(false)}>Anuluj</Button>
          <Button variant="contained" onClick={handleAddStaff}>
            Dodaj pracownika
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
